import { useEffect, useRef, useState } from "react";
import { LuEye, LuEyeOff } from "react-icons/lu";
import { clsxm } from "@/utils/clsxm";

const SSN_LENGTH = 4;

const toDigits = (value: string) =>
  Array.from({ length: SSN_LENGTH }, (_, i) => value[i] ?? "");

interface IProps {
  /** The current 4-digit string (may be shorter while typing) */
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  disabled?: boolean;
}

// Four single-digit boxes with a masked "•••–••–" prefix, matching the SSN2
// design. Digits are masked by default with a Show/Hide reveal toggle.
const SsnDigitsInput = ({ value, onChange, error, disabled }: IProps) => {
  const [revealed, setRevealed] = useState(false);
  const [focusIdx, setFocusIdx] = useState(-1);
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  // Positional per-box state — a plain join would collapse a cleared box and
  // shift the remaining digits left, so keep each box in its own slot.
  const [digits, setDigits] = useState<string[]>(() => toDigits(value));

  // Resync when the value prop changes from outside this component.
  useEffect(() => {
    if (value !== digits.join("")) setDigits(toDigits(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const focusBox = (i: number) => {
    const el = inputs.current[i];
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const commit = (next: string[]) => {
    setDigits(next);
    onChange(next.join(""));
  };

  const setDigit = (i: number, digit: string) => {
    const next = digits.slice();
    next[i] = digit;
    commit(next);
  };

  const handleInput = (i: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) {
      setDigit(i, "");
      return;
    }
    setDigit(i, raw.slice(-1));
    if (i < SSN_LENGTH - 1) focusBox(i + 1);
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (digits[i]) {
        // Clear this box only; the other boxes keep their positions.
        setDigit(i, "");
      } else if (i > 0) {
        e.preventDefault();
        setDigit(i - 1, "");
        focusBox(i - 1);
      }
    } else if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      focusBox(i - 1);
    } else if (e.key === "ArrowRight" && i < SSN_LENGTH - 1) {
      e.preventDefault();
      focusBox(i + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const txt = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, SSN_LENGTH);
    if (!txt) return;
    commit(toDigits(txt));
    focusBox(Math.min(txt.length, SSN_LENGTH - 1));
  };

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-[#303030]">
          Last 4 digits of SSN
        </span>
        <button
          type="button"
          onClick={() => setRevealed((prev) => !prev)}
          className="flex items-center gap-1.5 text-[13px] font-medium text-[#4f41a6]"
          aria-label={revealed ? "Hide SSN digits" : "Show SSN digits"}
        >
          {revealed ? <LuEyeOff size={16} /> : <LuEye size={16} />}
          <span>{revealed ? "Hide" : "Show"}</span>
        </button>
      </div>

      <div className="flex w-full items-center gap-[10px]">
        <span
          className="flex shrink-0 select-none items-center gap-1.5 text-lg font-semibold sm:text-[26px]"
          aria-hidden
        >
          <span className="tracking-[2px] text-[#aab0ad]">•••</span>
          <span className="font-normal text-[#d7dad7]">–</span>
          <span className="tracking-[2px] text-[#aab0ad]">••</span>
          <span className="font-normal text-[#d7dad7]">–</span>
        </span>
        <div className="flex min-w-0 flex-1 gap-2 sm:gap-3">
          {digits.map((digit, i) => {
            const filled = digit !== "";
            const focused = focusIdx === i;
            return (
              <input
                key={i}
                ref={(el) => (inputs.current[i] = el)}
                value={digit === "" ? "" : revealed ? digit : "•"}
                onChange={(e) => handleInput(i, e)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={handlePaste}
                onFocus={() => setFocusIdx(i)}
                onBlur={() => setFocusIdx((cur) => (cur === i ? -1 : cur))}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={1}
                disabled={disabled}
                aria-label={`SSN digit ${i + 1} of ${SSN_LENGTH}`}
                className={clsxm(
                  "h-[56px] min-w-0 flex-1 rounded-2xl border-2 bg-white text-center text-xl font-semibold text-charcoal outline-none transition-colors sm:h-[74px] sm:text-3xl",
                  error
                    ? "border-[#cd0707] bg-[#fbe2e2]"
                    : focused
                    ? "border-[#4f41a6]"
                    : filled
                    ? "border-[#6160b0] bg-[rgba(97,96,176,0.08)]"
                    : "border-[#e7eae7]"
                )}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export { SsnDigitsInput };
